/**
 * OnboardingHeader — Flint wordmark · centered Stepper · right-side action.
 *
 * The wordmark and the lens icon are inlined SVG so we don't pay for an icon
 * dep just for the brand mark. The right-side slot accepts any node so routes
 * can pass a `<Link to="…">` (TanStack Router) without this component
 * depending on a router.
 */
import * as React from "react";
import { cn } from "@flint/ui";

export interface OnboardingHeaderProps extends React.HTMLAttributes<HTMLElement> {
  /** Centered stepper or other progress indicator. */
  center?: React.ReactNode;
  /** Right-aligned slot — typically a "Back", "Skip", or "Change number" link. */
  right?: React.ReactNode;
  /** Optional wordmark href. Omit it when the wordmark is only branding. */
  wordmarkHref?: string;
  /** Slot under the wordmark, useful when consumer wants a Link wrapper. */
  wordmark?: React.ReactNode;
}

export function OnboardingHeader({
  center,
  right,
  wordmarkHref,
  wordmark,
  className,
  ...props
}: OnboardingHeaderProps) {
  return (
    <header className={cn("lo-header", className)} {...props}>
      <div className="lo-header-left">
        {wordmark ?? (wordmarkHref ? <Wordmark href={wordmarkHref} /> : <Wordmark />)}
      </div>
      <div className="lo-header-center">{center}</div>
      <div className="lo-header-right">{right}</div>
    </header>
  );
}

function Wordmark({ href }: { href?: string }) {
  const contents = (
    <>
      <span aria-hidden className="inline-flex">
        <svg viewBox="0 0 20 20" fill="none" width="20" height="20">
          <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="10" cy="10" r="2.5" fill="#0284C7" />
        </svg>
      </span>
      <span className="font-semibold tracking-tight">Flint</span>
      <span className="text-muted text-xs">Flint</span>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        aria-label="Flint"
        className="inline-flex items-center gap-2 text-ink no-underline"
      >
        {contents}
      </a>
    );
  }

  return (
    <span aria-label="Flint" className="inline-flex items-center gap-2 text-ink">
      {contents}
    </span>
  );
}
